import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { datadogRum } from '@datadog/browser-rum';
import type { RumEvent, Site, TrackingConsent } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { angularPlugin } from '@datadog/browser-rum-angular';
import { environment } from 'src/environments/environment';
import { OWNING_TEAM, scrubEvent, scrubLog } from './datadog-redaction';

const OCC_HOST_META_PLACEHOLDER = 'OCC_BACKEND_BASE_URL_VALUE';

export type DatadogEnv = 'development' | 'test' | 'preproduction' | 'production';

/**
 * Maps a CCv2 deployment tier to a Datadog `env` tag based on the OCC backend
 * host CCv2 injects per environment. Mirrors the rule shape used for
 * eCommerce-us (DCDSGlobalECOM-VSH-VAX-US), but region-agnostic (`\w+`
 * instead of `us`) since this app serves the NA subscription (Canada +
 * SanofiShop, e.g. `api.ca-p1.sanofiecomm.com`), not just US hosts.
 *
 * This repo's own hosts use two different tier-naming conventions, not just
 * eCommerce-us's digit-suffix one — see environment.ts (`emea-d1`) and
 * index.html's SmartEdit allow-origin list (`emea-stg1`, `emea-prd`). Every
 * rule below covers both forms (`-p\d` and literal `-prd`, etc.) per tier.
 *
 * Order matters: the first matching rule wins.
 */
export const OCC_HOST_ENV_RULES: ReadonlyArray<{ test: RegExp; env: DatadogEnv }> = [
  { test: /api\.\w+-(p\d+|prd)\b/, env: 'production' },
  { test: /-p\d+-public\./, env: 'production' },
  { test: /api\.\w+-(s1|stg1)\b/, env: 'preproduction' },
  { test: /-s1-public\./, env: 'preproduction' },
  { test: /api\.\w+-(s2|qa1)\b/, env: 'test' },
  { test: /-s2-public\./, env: 'test' },
  { test: /api\.\w+-d\d+\b/, env: 'development' },
  { test: /-d\d+-public\./, env: 'development' },
];

export function resolveDatadogEnv(occUrl: string, defaultEnv: DatadogEnv): DatadogEnv {
  const source = occUrl.trim().toLowerCase();
  if (!source) {
    return defaultEnv;
  }
  return OCC_HOST_ENV_RULES.find((rule) => rule.test.test(source))?.env ?? defaultEnv;
}

/**
 * Browser-reported CSP violations for blocked third-party trackers surface
 * as RUM errors even though nothing in the app broke — they dominate error
 * volume and mask real bugs. Same fix as oneshop-ecom (USONESHOP-3643) and
 * the VSH/VAX spartacusstore (USONESHOP-3875).
 */
export function isCspViolationEvent(event: RumEvent): boolean {
  return event.type === 'error' && !!event.error?.message?.startsWith('csp_violation:');
}

/**
 * OneTrust's Performance/Analytics cookie category for this tenant.
 * Confirmed by OneTrust support (USONESHOP-3916, ticket RITM27004509) to be
 * defined once at the tenant level, not per-site — the same value applies
 * to every storefront under this account, including this one.
 */
export const ONETRUST_PERFORMANCE_CATEGORY = 'C0002';

/**
 * Reads OneTrust's own consent state rather than trusting anything cached —
 * `OnetrustActiveGroups` is the data layer variable OneTrust itself
 * populates once a domain-ID match succeeds and the user has made (or
 * previously made) a choice. See USONESHOP-3916: this is currently empty
 * because of a separate domain-ID mismatch bug, so this will correctly
 * report "no consent" until that's fixed too — this function isn't wrong,
 * the upstream data just isn't populated yet.
 */
export function hasPerformanceConsent(activeGroups: string | null | undefined): boolean {
  return !!activeGroups?.split(',').includes(ONETRUST_PERFORMANCE_CATEGORY);
}

function currentTrackingConsent(): TrackingConsent {
  return hasPerformanceConsent((window as any).OnetrustActiveGroups) ? 'granted' : 'not-granted';
}

/**
 * Resolves the OCC backend base URL from the same deploy-injected meta tag
 * the rest of the app reads, falling back to environment.occBaseUrl. A plain
 * exported function (rather than reading the DOM inline) so it's unit
 * testable without a TestBed/DOM fixture.
 */
export function resolveOccBackendBaseUrl(occBaseUrlFallback: string | null, metaContent?: string | null): string {
  const injected = metaContent?.trim();
  if (injected && injected !== OCC_HOST_META_PLACEHOLDER) {
    return injected;
  }
  return occBaseUrlFallback ?? '';
}

/**
 * Initializes Datadog RUM + Logs for this app (SanofiShop / ssspartacusstore)
 * using Datadog's Tracking Consent API, kept in sync with the user's actual
 * OneTrust Performance-cookie consent rather than unconditional on bootstrap
 * (USONESHOP-3916: shipping RUM the same unguarded way GTM/GA already load
 * would repeat that gap, not close it).
 *
 * The SDKs are always `init()`ed once — with `trackingConsent` reflecting
 * whatever OneTrust reports at that moment — because the Tracking Consent
 * API is designed for exactly this: with `not-granted`, the SDKs collect
 * nothing persisted/sent until consent flips to `granted` via
 * `setTrackingConsent()`. This (rather than delaying `init()` itself) is
 * also what correctly handles a user *revoking* consent mid-session:
 * `OptanonWrapper` re-evaluates and calls `setTrackingConsent()` on every
 * OneTrust consent change, in both directions, not just the first grant.
 *
 * Bootstrapped only from `AppBrowserModule` (see main.ts), never from
 * `AppServerModule`, so the `isPlatformBrowser` check below is a second,
 * defense-in-depth guard rather than the only thing preventing server-side
 * init.
 */
@Injectable({
  providedIn: 'root',
})
export class DatadogRumService {
  private readonly platformId = inject(PLATFORM_ID);

  init(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const { applicationId, clientToken, site, service } = environment.datadog;
    if (!applicationId || !clientToken) {
      // Placeholders not filled in yet — no-op instead of sending garbage data.
      return;
    }

    const env = this.resolveEnv();
    const ddSite = site as Site;

    datadogRum.init({
      applicationId,
      clientToken,
      site: ddSite,
      service,
      env,
      trackingConsent: currentTrackingConsent(),
      sessionSampleRate: 100,
      sessionReplaySampleRate: 10,
      defaultPrivacyLevel: 'mask-user-input',
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      // Drops CSP-violation noise, then events with unscrubbable credentials, and redacts credentials in URLs and error
      // text (reset / OTP / SSO links).
      beforeSend: (event) => !isCspViolationEvent(event) && scrubEvent(event),
      plugins: [angularPlugin({ router: true })],
    });
    // Owning team on every event, for filtering and cost attribution in Datadog.
    datadogRum.setGlobalContextProperty('team', OWNING_TEAM);

    datadogLogs.init({
      clientToken,
      site: ddSite,
      service,
      env,
      trackingConsent: currentTrackingConsent(),
      forwardErrorsToLogs: true,
      // Redacts credentials in log messages, error stacks and urls (reset / OTP / SSO links).
      beforeSend: scrubLog,
      sessionSampleRate: 100,
    });
    datadogLogs.setGlobalContextProperty('team', OWNING_TEAM);

    const previousWrapper = (window as any).OptanonWrapper;
    (window as any).OptanonWrapper = () => {
      previousWrapper?.();
      const consent = currentTrackingConsent();
      datadogRum.setTrackingConsent(consent);
      datadogLogs.setTrackingConsent(consent);
    };
  }

  private resolveEnv(): DatadogEnv {
    const metaContent = document.querySelector('meta[name="occ-backend-base-url"]')?.getAttribute('content');
    const occUrl = resolveOccBackendBaseUrl(environment.occBaseUrl, metaContent);

    return resolveDatadogEnv(occUrl, (environment.datadog.defaultEnv ?? 'development') as DatadogEnv);
  }
}
